// src/services/authService.js
import { supabase } from './supabase'

// ==========================================
// 1. 核心認證流程 (SMS OTP)
// ==========================================

/**
 * 發送手機驗證碼 (OTP)
 * 適用於登入和註冊 (Supabase 會自動處理新舊用戶)
 * @param {string} phoneNumber - 格式建議為 E.164 (例如 +16041234567)
 */
export const sendOtp = async (phoneNumber) => {
  try {
    const { data, error } = await supabase.auth.signInWithOtp({
      phone: phoneNumber,
      options: {
        // 若是新號碼，允許自動創建 Auth 帳號
        shouldCreateUser: true 
      }
    });

    if (error) throw error;

    return { success: true, data };
  } catch (error) {
    // 友善化錯誤訊息 — 回傳 i18n key 讓前端翻譯
    const msg = error.message || '';
    if (msg.includes('not a valid phone number') || msg.includes('21211')) {
      return { success: false, errorKey: 'error_invalid_phone' };
    }
    if (msg.includes('Signups not allowed')) {
      return { success: false, errorKey: 'error_signups_closed' };
    }
    if (msg.includes('rate limit') || msg.includes('Too many requests') || msg.includes('429')) {
      return { success: false, errorKey: 'error_rate_limit' };
    }
    return { success: false, errorKey: 'error_send_failed' };
  }
};

/**
 * 驗證手機驗證碼
 * @param {string} phoneNumber - 手機號碼 (必須與發送時一致)
 * @param {string} token - 用戶輸入的 6 位數驗證碼
 */
export const verifyOtp = async (phoneNumber, token) => {
  try {
    const { data, error } = await supabase.auth.verifyOtp({
      phone: phoneNumber,
      token: token,
      type: 'sms'
    });

    if (error) throw error;

    if (data.user) {
      // 關鍵步驟：登入成功後，確保 public.users 資料表中有該用戶的資料
      await ensureUserProfile(data.user.id, phoneNumber);
    }

    return { success: true, user: data.user, session: data.session };
  } catch (error) {
    return { success: false, errorKey: 'error_otp_invalid' };
  }
};

/**
 * 登出
 */
export const signOut = async () => {
  try {
    const { error } = await supabase.auth.signOut()

    if (error) throw error

    return { success: true }
  } catch (error) {
    return { success: false, error: error.message }
  }
};

/**
 * 獲取當前 Session
 */
export const getSession = async () => {
  try {
    const { data: { session }, error } = await supabase.auth.getSession()

    if (error) throw error

    return { success: true, session }
  } catch (error) {
    return { success: false, error: error.message }
  }
};

// ==========================================
// 2. 資料庫用戶資料管理 (public.users)
// ==========================================

/**
 * 內部輔助函數：確保用戶在 public.users 表中有資料
 * 在 verifyOtp 成功後自動呼叫
 */
const ensureUserProfile = async (userId, phoneNumber) => {
  try {
    // 1. 檢查資料是否已存在
    const { data: existingUser } = await supabase
      .from('users')
      .select('id')
      .eq('id', userId)
      .single();

    if (existingUser) {
      return;
    }

    // 2. 如果不存在，則創建 (為了相容舊邏輯，我們仍產生一個 placeholder email)
    const fakeEmail = `${phoneNumber}@sms.user`; 

    const { error } = await supabase
      .from('users')
      .insert({
        id: userId,
        phone_number: phoneNumber,
        is_verified: true, // 通過 OTP 驗證即視為已驗證
        email: fakeEmail,  // 保持資料庫欄位完整性
        created_at: new Date().toISOString()
      });

    if (error) {
      // 忽略 "duplicate key" 錯誤 (防止併發請求導致的錯誤)
      if (error.code === '23505') {
      } else {
      }
    } else {
    }
  } catch (error) {
    // silently ignore
  }
};

/**
 * 獲取用戶資料
 */
export const getUserProfile = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', userId)
      .single()

    if (error) throw error

    return { success: true, data }
  } catch (error) {
    return { success: false, error: error.message }
  }
};

/**
 * 更新用戶資料
 */
export const updateUserProfile = async (userId, updates) => {
  try {
    // Whitelist allowed fields to prevent privilege escalation (e.g. setting role)
    const ALLOWED_FIELDS = ['name', 'phone_number', 'email', 'avatar_url'];
    const safeUpdates = {};
    for (const key of Object.keys(updates)) {
      if (ALLOWED_FIELDS.includes(key)) {
        safeUpdates[key] = updates[key];
      }
    }
    if (Object.keys(safeUpdates).length === 0) {
      return { success: false, error: 'No valid fields to update' };
    }

    const { data, error } = await supabase
      .from('users')
      .update(safeUpdates)
      .eq('id', userId)
      .select()
      .single()

    if (error) throw error

    return { success: true, data }
  } catch (error) {
    return { success: false, error: error.message }
  }
};